/**
 * Accessibility utility functions for EAA compliance
 * Handles keyboard navigation, focus management, and ARIA announcements
 */

/**
 * Global keyboard event manager for accessibility
 */
class AccessibilityKeyboardManager {
    constructor() {
        this.escapeCallbacks = new Map();
        this.shortcuts = new Map();
        this.isInitialized = false;
    }

    /**
     * Initialize global keyboard event listeners
     */
    init() {
        if (this.isInitialized) return;

        document.addEventListener('keydown', this.handleGlobalKeydown.bind(this));
        this.isInitialized = true;
    }

    /**
     * Handle global keyboard events
     * @param {KeyboardEvent} event 
     */
    handleGlobalKeydown(event) {
        const { key, ctrlKey, altKey, metaKey } = event;

        // Handle Escape key for modal/dialog dismissal
        if (key === 'Escape') {
            this.handleEscapeKey(event);
        }

        // Handle keyboard shortcuts
        const shortcutKey = this.getShortcutKey(key, ctrlKey, altKey, metaKey);
        if (this.shortcuts.has(shortcutKey)) {
            event.preventDefault();
            this.shortcuts.get(shortcutKey)(event);
        }
    }

    /**
     * Register escape key callback for component
     * @param {string} componentId - Unique identifier for component
     * @param {Function} callback - Function to call on escape
     * @param {number} priority - Higher priority callbacks execute first
     */
    registerEscapeCallback(componentId, callback, priority = 0) {
        this.escapeCallbacks.set(componentId, { callback, priority });
    }

    /**
     * Unregister escape key callback
     * @param {string} componentId 
     */
    unregisterEscapeCallback(componentId) {
        this.escapeCallbacks.delete(componentId);
    }

    /**
     * Handle escape key press - call registered callbacks by priority
     * @param {KeyboardEvent} event 
     */
    handleEscapeKey(event) {
        const callbacks = Array.from(this.escapeCallbacks.values())
            .sort((a, b) => b.priority - a.priority);

        for (const { callback } of callbacks) {
            if (callback(event)) {
                // If callback returns true, stop propagation
                break;
            }
        }
    }

    /**
     * Register keyboard shortcut
     * @param {string} key - Key combination (e.g., 'ctrl+s', 'alt+m')
     * @param {Function} callback - Function to execute
     */
    registerShortcut(key, callback) {
        this.shortcuts.set(key, callback);
    }

    /**
     * Unregister keyboard shortcut
     * @param {string} key 
     */
    unregisterShortcut(key) {
        this.shortcuts.delete(key);
    }

    /**
     * Generate shortcut key string
     * @param {string} key 
     * @param {boolean} ctrlKey 
     * @param {boolean} altKey 
     * @param {boolean} metaKey 
     * @returns {string}
     */
    getShortcutKey(key, ctrlKey, altKey, metaKey) {
        const modifiers = [];
        if (ctrlKey) modifiers.push('ctrl');
        if (altKey) modifiers.push('alt');
        if (metaKey) modifiers.push('meta');
        
        return modifiers.length > 0 ? `${modifiers.join('+')}+${key.toLowerCase()}` : key.toLowerCase();
    }
}

/**
 * Focus management utilities
 */
export const FocusManager = {
    /**
     * Set focus with fallback and error handling
     * @param {Element|string} element - Element or selector
     * @param {Object} options - Focus options
     */
    setFocus(element, options = {}) {
        const target = typeof element === 'string' ? document.querySelector(element) : element;
        
        if (!target) {
            console.warn('Focus target not found:', element);
            return false;
        }

        try {
            // Make element focusable if needed
            if (!this.isFocusable(target)) {
                target.setAttribute('tabindex', '-1');
            }

            target.focus(options);
            
            // Announce focus change to screen readers if specified
            if (options.announce) {
                this.announceToScreenReader(options.announce);
            }

            return true;
        } catch (error) {
            console.error('Error setting focus:', error);
            return false;
        }
    },

    /**
     * Check if element is focusable
     * @param {Element} element 
     * @returns {boolean}
     */
    isFocusable(element) {
        if (!element || element.disabled) return false;

        const focusableElements = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
            '[contenteditable]'
        ];

        return focusableElements.some(selector => element.matches(selector)) ||
               element.getAttribute('tabindex') === '0';
    },

    /**
     * Get all focusable elements within container
     * @param {Element} container 
     * @returns {Array<Element>}
     */
    getFocusableElements(container = document) {
        const focusableSelectors = [
            'a[href]:not([disabled])',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
            '[contenteditable]:not([contenteditable="false"])'
        ].join(', ');

        return Array.from(container.querySelectorAll(focusableSelectors))
            .filter(el => !el.hidden && el.offsetParent !== null);
    },

    /**
     * Trap focus within container (for modals)
     * @param {Element} container 
     * @returns {Function} cleanup function
     */
    trapFocus(container) {
        const focusableElements = this.getFocusableElements(container);
        if (focusableElements.length === 0) return () => {};

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        const handleTabKey = (event) => {
            if (event.key !== 'Tab') return;

            if (event.shiftKey) {
                // Shift + Tab
                if (document.activeElement === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                }
            } else {
                // Tab
                if (document.activeElement === lastElement) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        };

        container.addEventListener('keydown', handleTabKey);

        // Set initial focus
        this.setFocus(firstElement);

        // Return cleanup function
        return () => {
            container.removeEventListener('keydown', handleTabKey);
        };
    },

    /**
     * Announce message to screen readers
     * @param {string} message 
     * @param {string} priority - 'polite' or 'assertive'
     */
    announceToScreenReader(message, priority = 'polite') {
        const announcer = document.createElement('div');
        announcer.setAttribute('aria-live', priority);
        announcer.setAttribute('aria-atomic', 'true');
        announcer.className = 'sr-only';
        announcer.textContent = message;

        document.body.appendChild(announcer);

        // Remove after announcement
        setTimeout(() => {
            document.body.removeChild(announcer);
        }, 1000);
    }
};

/**
 * ARIA utilities
 */
export const AriaManager = {
    /**
     * Update ARIA attributes
     * @param {Element} element 
     * @param {Object} attributes 
     */
    setAttributes(element, attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
            if (value === null || value === undefined) {
                element.removeAttribute(key);
            } else {
                element.setAttribute(key, value);
            }
        });
    },

    /**
     * Create and manage live regions
     * @param {string} id 
     * @param {string} politeness - 'polite' or 'assertive'
     * @returns {Element}
     */
    createLiveRegion(id, politeness = 'polite') {
        let region = document.getElementById(id);
        
        if (!region) {
            region = document.createElement('div');
            region.id = id;
            region.setAttribute('aria-live', politeness);
            region.setAttribute('aria-atomic', 'true');
            region.className = 'sr-only';
            document.body.appendChild(region);
        }

        return region;
    },

    /**
     * Announce message in live region
     * @param {string} message 
     * @param {string} regionId 
     */
    announce(message, regionId = 'default-live-region') {
        const region = this.createLiveRegion(regionId);
        region.textContent = message;
    }
};

// Global instance
export const keyboardManager = new AccessibilityKeyboardManager();

// Initialize on load
if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => keyboardManager.init());
    } else {
        keyboardManager.init();
    }
}

export default {
    keyboardManager,
    FocusManager,
    AriaManager
};